"use client";

// Pre-warning before the maintenance lock engages.
//
// Best-effort by design: it rides on the heartbeat, which only polls while the
// tab is visible. A student who switched tabs and comes back after the lock
// gets no warning at all — that case is covered by the 503 state instead, not
// by adding a poll that costs battery around the clock.
//
// The countdown is driven by a server-supplied number of seconds, never by a
// timestamp the client interprets. A wrong system clock must not be able to
// render "closes in -47 minutes". Each heartbeat re-syncs `kvar` to the
// server's fresh value; the local interval only fills the seconds between
// polls.
//
// Never rendered when the lock is already on (`underhall: true`) — the caller
// is responsible for passing `varningSekunder={null}` in that case, since a
// countdown to an already-closed door is a lie.
//
// Built on the shared `Modal` component rather than a bespoke overlay: Modal
// is a plain controlled component (no internal timer, no state of its own
// tied to `message`), so passing it a `message` that recomputes every second
// just re-renders the text in place — it never closes and reopens. That gets
// Escape-to-close, click-outside-to-close, and aria-labelledby-against-the-
// heading for free, all of which a hand-rolled overlay would have to
// reimplement. `useModal()` (the Promise-based wrapper around Modal) was not
// used instead: its single resolver captures the message at call time and
// has no way to update it while the promise is pending, which is exactly
// what a live countdown needs — so this goes straight to `Modal`.
import { useCallback, useEffect, useRef, useState } from "react";
import Modal from "@/components/ui/Modal";

const FINAL_REMINDER_SEK = 60;

export function UnderhallVarning({ varningSekunder }: { varningSekunder: number | null }) {
  // `kvar`/`raknarNer` are pure derivations of the `varningSekunder` prop.
  // Deriving them during render (guarded by comparing against the previous
  // prop value) instead of in a useEffect avoids the extra render pass an
  // effect-based sync would cost, and is the pattern React's own docs
  // recommend for "adjust state when a prop changes" — see
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes.
  const [previousVarningSekunder, setPreviousVarningSekunder] = useState(varningSekunder);
  const [kvar, setKvar] = useState<number | null>(varningSekunder);
  const [raknarNer, setRaknarNer] = useState(varningSekunder !== null);
  const [stangd, setStangd] = useState(false);
  const harVisatSist = useRef(false);
  // Mirrors `kvar` for the interval below to read without a stale closure.
  // Kept in sync by a plain (non-setState) ref write in an effect — never
  // written during render, which `react-hooks/refs` disallows.
  const kvarRef = useRef(kvar);

  if (varningSekunder !== previousVarningSekunder) {
    setPreviousVarningSekunder(varningSekunder);
    setKvar(varningSekunder);
    setRaknarNer(varningSekunder !== null);
    // The window closed (locked, or the schedule was cleared) — reset so the
    // next window shows as a fresh warning. A window that is still counting
    // down (varningSekunder still a number, just refreshed) must NOT reset
    // `stangd` — that is the same warning, only resynced to the server's
    // fresh second count. `harVisatSist` is reset in the effect below, not
    // here — refs may only be mutated outside of render.
    if (varningSekunder === null) {
      setStangd(false);
    }
  }

  useEffect(() => {
    kvarRef.current = kvar;
  }, [kvar]);

  // A single interval that lives exactly as long as a countdown is running —
  // started when `raknarNer` becomes true, torn down by the cleanup function
  // when it becomes false or the component unmounts. `raknarNer` is a real
  // state variable (not an expression in the dependency array), so
  // exhaustive-deps holds.
  //
  // The tick callback computes `next` as a plain local value and calls
  // `setKvar`/`setStangd` directly — not from inside a `setKvar` updater
  // function. Updater functions must stay pure (React may invoke them twice
  // in StrictMode); reading the previous value from `kvarRef` instead keeps
  // the tick a normal, side-effecting callback rather than an impure updater.
  useEffect(() => {
    if (!raknarNer) {
      // No window is running — reset ahead of the next warning.
      harVisatSist.current = false;
      return;
    }
    const intervall = setInterval(() => {
      const previous = kvarRef.current;
      if (previous === null) return;
      const next = Math.max(0, previous - 1);
      kvarRef.current = next;
      setKvar(next);
      // Show the final reminder once, when crossing into the last minute —
      // even if the student already dismissed the first popup.
      if (next <= FINAL_REMINDER_SEK && !harVisatSist.current) {
        harVisatSist.current = true;
        setStangd(false);
      }
    }, 1000);
    return () => clearInterval(intervall);
  }, [raknarNer]);

  const close = useCallback(() => setStangd(true), []);

  if (kvar === null) return null;

  const min = Math.floor(kvar / 60);
  const sek = String(kvar % 60).padStart(2, "0");

  return (
    <Modal
      open={!stangd}
      titleId="underhall-varning-title"
      title="vektr stängs för underhåll"
      message={
        <>
          <p>
            Vi lägger in förbättringar om <strong>{min}:{sek}</strong>.
          </p>
          <p className="mt-2">
            Lämna in ditt svar nu så det hinner sparas. Du kan fortsätta
            arbeta till dess.
          </p>
        </>
      }
      confirmLabel="Jag förstår"
      confirmTitle="Stäng meddelandet och fortsätt arbeta"
      onConfirm={close}
    />
  );
}
